import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get token from localStorage — resiliente a diferentes fluxos de login
// (alguns salvam token dentro de `user`, outros salvam em chave própria)
const getToken = () => {
  const direct = localStorage.getItem('token');
  if (direct) return direct;
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const userData = JSON.parse(user);
      return userData.token || null;
    } catch { return null; }
  }
  return null;
};

/**
 * uploadFileToS3 — helper único para todos os uploads S3 do admin.
 *
 * Cuida de:
 *   • Ler o token do lugar correto (independente do flow de login usado)
 *   • Montar o FormData (file + folder + campos extras opcionais)
 *   • Timeout generoso pra vídeos/PDFs grandes
 *   • Erros padronizados (retorna a mensagem do backend quando existe)
 *
 * @param {File} file - arquivo a enviar
 * @param {string} folder - pasta lógica no bucket (ex: 'marketplace', 'videos', 'documents')
 * @param {object} [opts]
 * @param {number} [opts.timeout=180000] - timeout em ms (default 3 min)
 * @param {(pct:number)=>void} [opts.onProgress] - callback de progresso 0..100
 * @param {object} [opts.extras] - pares chave/valor adicionais no FormData (ex: {skip_watermark: 'true'})
 * @returns {Promise<{url:string, key:string, size?:number}>}
 */
export const uploadFileToS3 = async (file, folder = 'misc', opts = {}) => {
  if (!file) throw new Error('Arquivo obrigatório');
  const fd = new FormData();
  fd.append('file', file);
  fd.append('folder', folder);
  if (opts.extras) {
    for (const [k, v] of Object.entries(opts.extras)) {
      fd.append(k, String(v));
    }
  }
  const token = getToken();
  const res = await axios.post(`${API}/upload/s3`, fd, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      'Content-Type': 'multipart/form-data',
    },
    timeout: opts.timeout ?? 180000,
    onUploadProgress: opts.onProgress
      ? (e) => {
          if (e.total) opts.onProgress(Math.round((e.loaded / e.total) * 100));
        }
      : undefined,
  });
  if (!res.data?.url) {
    throw new Error(res.data?.detail || 'Upload não retornou URL');
  }
  return res.data;
};

/**
 * uploadFileToS3ViaPresigned — sobe direto do browser pro S3 usando
 * uma URL pré-assinada (bypass CloudFront/CDN, ideal para uploads
 * que estão sendo bloqueados por WAF/limite de tamanho no proxy).
 *
 * Etapas:
 *   1) POST /api/upload/s3/presigned → recebe {presigned_url, final_url}
 *   2) PUT direto no `presigned_url` (S3 canónico, sem CDN entre)
 *   3) retorna { url: final_url, key }
 */
export const uploadFileToS3ViaPresigned = async (file, folder = 'misc', opts = {}) => {
  if (!file) throw new Error('Arquivo obrigatório');
  const token = getToken();

  // 1) pega presigned URL
  const fd = new FormData();
  fd.append('filename', file.name || 'upload.bin');
  fd.append('content_type', file.type || 'application/octet-stream');
  fd.append('folder', folder);
  const step1 = await axios.post(`${API}/upload/s3/presigned`, fd, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      'Content-Type': 'multipart/form-data',
    },
    timeout: 30000,
  });
  const { presigned_url, final_url, key } = step1.data || {};
  if (!presigned_url || !final_url) {
    throw new Error(step1.data?.detail || 'Presigned URL não retornou.');
  }

  // 2) PUT direto no S3 (bypass CDN)
  await axios.put(presigned_url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    timeout: opts.timeout ?? 300000,
    // Importante: NÃO enviar cabeçalho Authorization no S3 (a URL já autentica)
    transformRequest: [(data, headers) => {
      delete headers.Authorization;
      delete headers.common;
      return data;
    }],
    onUploadProgress: opts.onProgress
      ? (e) => { if (e.total) opts.onProgress(Math.round((e.loaded / e.total) * 100)); }
      : undefined,
  });

  return { url: final_url, key, size: file.size };
};

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ═══════════════════════════════════════════════════════════════════════
// Detecção de drift de deploy em real-time (X-App-Version)
// ═══════════════════════════════════════════════════════════════════════
// Backend injeta X-App-Version em toda resposta /api/*. Comparamos o valor
// da PRIMEIRA resposta (boot) com o das respostas subsequentes; se mudou,
// significa que rolou um deploy e o bundle atual está desatualizado.
// Ao invés de esperar o polling de 15min do PWA, notificamos o SW na hora
// via window.__notifyVersionDrift(newVersion), que dispara registration.update()
// e mostra o toast "Nova versão disponível" pro usuário.
let _bootAppVersion = null;
let _lastDriftAt = 0;
const DRIFT_COOLDOWN_MS = 60 * 1000; // 1 notificação por minuto no máximo
function _checkVersionDrift(response) {
  try {
    // axios normaliza headers em lowercase
    const headers = response?.headers || {};
    const v = headers['x-app-version'] || headers['X-App-Version'];
    if (!v) return;
    if (_bootAppVersion === null) {
      _bootAppVersion = v;
      try {
        if (typeof window !== 'undefined') window.__bootAppVersion = v;
      } catch { /* noop */ }
      return;
    }
    if (v !== _bootAppVersion) {
      const now = Date.now();
      if ((now - _lastDriftAt) < DRIFT_COOLDOWN_MS) return; // debounce
      _lastDriftAt = now;
      // Atualiza a referência pra não redisparar até haver NOVO deploy
      const prev = _bootAppVersion;
      _bootAppVersion = v;
      // eslint-disable-next-line no-console
      console.info(`[api] drift de deploy detectado: ${prev} → ${v}`);
      try {
        if (typeof window !== 'undefined' && typeof window.__notifyVersionDrift === 'function') {
          window.__notifyVersionDrift(v);
        }
      } catch { /* noop — pwaRegistration ainda não montou */ }
    }
  } catch { /* noop */ }
}

// Response interceptor for better error handling
apiClient.interceptors.response.use(
  (response) => {
    _checkVersionDrift(response);
    return response;
  },
  (error) => {
    // Mesmo em erro (4xx/5xx com body válido), o header X-App-Version vem —
    // então detectamos drift também em respostas de erro.
    if (error?.response) _checkVersionDrift(error.response);
    // Auto-logout on 401 (token expirado/inválido)
    // Evita loop na própria tela de login
    if (error.response?.status === 401) {
      handleUnauthorized(error);
    } else if (error.response?.status === 403) {
      handleForbidden(error);
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    } else if (!error.response) {
      console.error('Network error');
    }
    return Promise.reject(error);
  }
);

// Helper global para expiração de token — usado também pelo interceptor global do axios
let _unauthorizedHandled = false;
let _forbiddenNotified = false;
function handleUnauthorized(error) {
  if (_unauthorizedHandled) return;
  // Defesa em profundidade: NÃO deslogar se o request foi feito SEM header Authorization.
  // Isso indica um bug de código (chamada esqueceu de mandar o token) e não uma sessão expirada real —
  // antes essa falha derrubava o usuário logado para a tela de login.
  try {
    const authHeader = error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
    if (!authHeader) {
      // eslint-disable-next-line no-console
      console.warn('[auth] 401 ignorado: request sem Authorization header — bug de chamada, não sessão expirada', error?.config?.url);
      return;
    }
  } catch (_) { /* ignore */ }
  const path = window.location.pathname;
  const onAuthPage = ['/login', '/register', '/esqueci-senha', '/reset-password', '/alterar-senha']
    .some(p => path.startsWith(p));
  if (onAuthPage) return;
  _unauthorizedHandled = true;
  // eslint-disable-next-line no-console
  console.warn('[auth] 401 detected, clearing session and redirecting to /login');
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  window.location.href = '/login?reason=expired';
}

// Interceptor global no axios raiz para capturar chamadas feitas com `import axios from 'axios'`
// (muitos componentes usam axios direto, não o apiClient).
axios.interceptors.response.use(
  (response) => {
    _checkVersionDrift(response);
    return response;
  },
  (error) => {
    if (error?.response) _checkVersionDrift(error.response);
    if (error.response?.status === 401) {
      handleUnauthorized(error);
    } else if (error.response?.status === 403) {
      handleForbidden(error);
    }
    return Promise.reject(error);
  }
);

// Trata 403 detectando causa comum: sessão de impersonação ativa em rota admin,
// ou token stale mas com role diferente. Mostra mensagem clara ao invés de "Request failed with status code 403".
function handleForbidden(error) {
  if (_forbiddenNotified) return;
  const detail = error?.response?.data?.detail || '';
  // Detecta impersonação ativa (usuário admin fingindo ser não-admin, endpoint bloqueia)
  const isImpersonating = !!localStorage.getItem('impersonating') || !!localStorage.getItem('impersonation_original_token');
  // Sinaliza uma vez a cada request para não repetir o toast
  _forbiddenNotified = true;
  setTimeout(() => { _forbiddenNotified = false; }, 3000);
  try {
    const msg = isImpersonating
      ? 'Você está impersonando outro usuário. Rotas de admin ficam bloqueadas durante impersonação — saia da impersonação para salvar.'
      : (detail && typeof detail === 'string'
        ? `Acesso negado: ${detail}`
        : 'Acesso negado (403). Sua sessão pode ter perdido privilégios de admin — faça logout e login novamente.');
    // Envia via window event; o Toaster do app pode capturar. Fallback: console.
    window.dispatchEvent(new CustomEvent('app:auth-forbidden', { detail: { message: msg, url: error?.config?.url } }));
    // eslint-disable-next-line no-console
    console.warn('[auth] 403 forbidden:', msg, 'url:', error?.config?.url);
  } catch (_) { /* ignore */ }
}

// Auth API
export const authAPI = {
  register: async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    return response.data;
  },
  
  googleLogin: async (token) => {
    const response = await axios.post(`${API}/auth/google`, { token });
    return response.data;
  },
  
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },
  
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/auth/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // MFA endpoints
  mfaSetup: async () => {
    const response = await apiClient.post('/mfa/setup');
    return response.data;
  },

  mfaVerify: async (token) => {
    const response = await apiClient.post('/mfa/verify', { token });
    return response.data;
  },

  mfaValidate: async (userId, token) => {
    const response = await axios.post(`${API}/mfa/validate`, { userId, token });
    return response.data;
  },

  mfaDisable: async (password) => {
    const response = await apiClient.delete('/mfa/disable', { data: { password } });
    return response.data;
  },

  mfaStatus: async () => {
    const response = await apiClient.get('/mfa/status');
    return response.data;
  },

  mfaListTrustedDevices: async (currentToken) => {
    const params = currentToken ? { current_token: currentToken } : {};
    const response = await apiClient.get('/mfa/trusted-devices', { params });
    return response.data;
  },

  mfaRevokeTrustedDevice: async (deviceId) => {
    const response = await apiClient.delete(`/mfa/trusted-devices/${deviceId}`);
    return response.data;
  },

  mfaRevokeAllTrustedDevices: async () => {
    const response = await apiClient.delete('/mfa/trusted-devices');
    return response.data;
  },

  loginWithMFA: async (email, password, mfaToken, captchaToken, deviceToken, trustDevice) => {
    const body = { email, password, mfaToken };
    if (captchaToken) body.captcha_token = captchaToken;
    if (deviceToken) body.deviceToken = deviceToken;
    if (trustDevice) body.trustDevice = true;
    const response = await axios.post(`${API}/auth/login`, body);
    return response.data;
  },
};

// Content API (Conteúdos)
export const videoAPI = {
  getVideos: async (params = {}) => {
    const response = await axios.get(`${API}/conteudos`, { params });
    return response.data;
  },
  // Alias for Portuguese naming convention
  getVídeos: async (params = {}) => {
    const response = await axios.get(`${API}/conteudos`, { params });
    return response.data;
  },
  
  getVideo: async (id) => {
    const response = await axios.get(`${API}/conteudos/${id}`);
    return response.data;
  },
  
  createVideo: async (videoData) => {
    const response = await apiClient.post('/conteudos', videoData);
    return response.data;
  },
  
  updateVideo: async (id, videoData) => {
    const response = await apiClient.put(`/conteudos/${id}`, videoData);
    return response.data;
  },
  
  deleteVideo: async (id) => {
    const response = await apiClient.delete(`/conteudos/${id}`);
    return response.data;
  },

  batchDelete: async (ids) => {
    const response = await apiClient.post('/conteudos/batch-delete', { ids });
    return response.data;
  },

  checkDependencies: async (id) => {
    const response = await apiClient.get(`/conteudos/${id}/dependencies`);
    return response.data;
  },
  
  incrementView: async (id) => {
    const response = await axios.post(`${API}/conteudos/${id}/view`);
    return response.data;
  },
  
  getRelatedVideos: async (id) => {
    const response = await axios.get(`${API}/conteudos/${id}/related`);
    return response.data;
  },
  
  setRelatedVideos: async (videoId, relatedVideoIds) => {
    const response = await apiClient.put(`/conteudos/${videoId}/related`, { relatedOffers: [], relatedContent: relatedVideoIds });
    return response.data;
  },

  // Update visibility
  updateVisibility: async (videoId, visible) => {
    const response = await apiClient.put(`/conteudos/${videoId}/visibility`, { visible_in_home: visible });
    return response.data;
  },

  // Update status
  updateStatus: async (videoId, status) => {
    const response = await apiClient.put(`/conteudos/${videoId}/status`, { status });
    return response.data;
  },
  
  uploadThumbnail: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/conteudos/upload-thumbnail', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Personas endpoints
  getPersonas: async (videoId) => {
    const response = await apiClient.get(`/conteudos/${videoId}/personas`);
    return response.data;
  },
  
  addPersona: async (videoId, personaId) => {
    const response = await apiClient.post(`/conteudos/${videoId}/personas/${personaId}`);
    return response.data;
  },
  
  removePersona: async (videoId, personaId) => {
    const response = await apiClient.delete(`/conteudos/${videoId}/personas/${personaId}`);
    return response.data;
  },

  // Children (child content) endpoints
  getChildren: async (videoId) => {
    const response = await axios.get(`${API}/conteudos/${videoId}/children`);
    return response.data;
  },

  getAvailableParents: async () => {
    const response = await apiClient.get('/conteudos/available-parents/list');
    return response.data;
  },

  attachChild: async (parentId, childId) => {
    const response = await apiClient.post(`/conteudos/${parentId}/children/attach`, { child_id: childId });
    return response.data;
  },

  detachChild: async (parentId, childId) => {
    const response = await apiClient.post(`/conteudos/${parentId}/children/detach`, { child_id: childId });
    return response.data;
  },

  reorderChildren: async (parentId, order) => {
    const response = await apiClient.put(`/conteudos/${parentId}/children/reorder`, { order });
    return response.data;
  },

  // Prevention types endpoints
  getPreventionTypes: async (videoId) => {
    const response = await apiClient.get(`/conteudos/${videoId}/prevention-types`);
    return response.data;
  },
};

// Questionnaire API
export const questionnaireAPI = {
  getQuestionnaire: async (videoId) => {
    const response = await axios.get(`${API}/questionnaires/${videoId}`);
    return response.data;
  },
  
  createQuestionnaire: async (data) => {
    const response = await apiClient.post('/questionnaires', data);
    return response.data;
  },
  
  updateQuestionnaire: async (videoId, data) => {
    const response = await apiClient.put(`/questionnaires/${videoId}`, data);
    return response.data;
  },
  
  deleteQuestionnaire: async (videoId) => {
    const response = await apiClient.delete(`/questionnaires/${videoId}`);
    return response.data;
  },
  
  submitQuiz: async (videoId, answers) => {
    const response = await apiClient.post(`/questionnaires/${videoId}/submit`, { answers });
    return response.data;
  },
  
  generateWithAI: async (videoId, numQuestions = 5, passingScore = 70) => {
    const response = await apiClient.post(`/questionnaires/generate/${videoId}`, {
      num_questions: numQuestions,
      passing_score: passingScore,
    });
    return response.data;
  },
};

// Certificate API
export const certificateAPI = {
  getMyCertificates: async () => {
    const stored = localStorage.getItem('user');
    if (!stored) throw new Error('Not authenticated');
    const { user } = JSON.parse(stored);
    const response = await apiClient.get(`/certificates/user/${user.id}`);
    return response.data;
  },

  issueProgramCertificate: async (programId) => {
    const response = await apiClient.post(`/certificates/program/${programId}/issue`);
    return response.data;
  },
  
  getCertificate: async (id) => {
    const response = await apiClient.get(`/certificates/${id}`);
    return response.data;
  },
  
  downloadCertificate: async (id) => {
    try {
      console.log('Starting certificate download for ID:', id);
      
      // Cache-buster + no-store no cliente: garante que qualquer UPDATE feito no
      // certificado (ex.: correção manual de data via SQL) apareça imediatamente
      // no próximo download, sem servir do Service Worker (PWA) ou HTTP cache.
      const response = await apiClient.get(`/certificates/${id}/download`, {
        responseType: 'blob',
        params: { _ts: Date.now() },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      
      console.log('Response received:', {
        status: response.status,
        contentType: response.headers['content-type'],
        size: response.data.size
      });
      
      // Check if response has data
      if (!response.data || response.data.size === 0) {
        throw new Error('PDF vazio ou não gerado');
      }
      
      // Create blob with proper type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      console.log('Blob created:', { size: blob.size, type: blob.type });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `certificado_${id}.pdf`;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      console.log('Download filename:', filename);
      
      // Try using the modern File System Access API for Chrome if available
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'PDF Document',
              accept: { 'application/pdf': ['.pdf'] },
            }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          console.log('File saved using File System Access API');
          return true;
        } catch (err) {
          console.log('File System Access API failed or cancelled, falling back:', err.message);
          // Fall through to traditional method
        }
      }
      
      // Traditional method (works in most browsers including Edge)
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // For Chrome: dispatch a click event
      document.body.appendChild(link);
      
      // Use both click() and dispatchEvent for better compatibility
      if (document.createEvent) {
        const event = document.createEvent('MouseEvents');
        event.initEvent('click', true, true);
        link.dispatchEvent(event);
      } else {
        link.click();
      }
      
      console.log('Download link clicked');
      
      // Cleanup after a delay
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
        console.log('Cleanup completed');
      }, 250);
      
      return true;
    } catch (error) {
      console.error('Error in downloadCertificate:', error);
      throw error;
    }
  },
};

// Admin API
export const adminAPI = {
  getStats: async (params = {}) => {
    const response = await apiClient.get('/admin/stats', { params });
    return response.data;
  },
  
  getUsers: async (params = {}) => {
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },
  
  getCertificates: async (params = {}) => {
    const response = await apiClient.get('/admin/certificates', { params });
    return response.data;
  },
  
  updateUser: async (id, data) => {
    const response = await apiClient.put(`/admin/users/${id}`, data);
    return response.data;
  },
  
  deleteUser: async (id) => {
    const response = await apiClient.delete(`/admin/users/${id}`);
    return response.data;
  },

  enforceMFA: async (userId, enforce) => {
    const response = await apiClient.put(`/auth/mfa/enforce/${userId}`, { enforce });
    return response.data;
  },
};

// Companies API
export const companyAPI = {
  getAll: async () => {
    const response = await apiClient.get('/companies');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/companies/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/companies', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/companies/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/companies/${id}`);
    return response.data;
  },
  
  getVideos: async (id) => {
    const response = await apiClient.get(`/companies/${id}/conteudos`);
    return response.data;
  },
  // Alias for Portuguese naming convention
  getVídeos: async (id) => {
    const response = await apiClient.get(`/companies/${id}/conteudos`);
    return response.data;
  },
  
  setVideos: async (id, videoIds) => {
    const response = await apiClient.post(`/companies/${id}/conteudos`, { video_ids: videoIds });
    return response.data;
  },
  setVídeos: async (id, videoIds) => {
    const response = await apiClient.post(`/companies/${id}/conteudos`, { video_ids: videoIds });
    return response.data;
  },
};

// Programs API
export const programAPI = {
  getAll: async (filters) => {
    const response = await apiClient.get('/programs', { params: filters });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/programs/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/programs', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/programs/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/programs/${id}`);
    return response.data;
  },
  duplicate: async (id) => {
    const response = await apiClient.post(`/programs/${id}/duplicate`);
    return response.data;
  },
  
  getVideos: async (id) => {
    const response = await apiClient.get(`/programs/${id}/conteudos`);
    return response.data;
  },
  // Alias for Portuguese naming convention
  getVídeos: async (id) => {
    const response = await apiClient.get(`/programs/${id}/conteudos`);
    return response.data;
  },
  
  addVideos: async (id, videoIds) => {
    const response = await apiClient.post(`/programs/${id}/conteudos`, { video_ids: videoIds });
    return response.data;
  },
  
  getUsers: async (id) => {
    const response = await apiClient.get(`/programs/${id}/users`);
    return response.data;
  },
  
  addUsers: async (id, userIds) => {
    const response = await apiClient.post(`/programs/${id}/users`, { user_ids: userIds });
    return response.data;
  },
  
  getDashboard: async (id) => {
    const response = await apiClient.get(`/programs/${id}/dashboard`);
    return response.data;
  },

  generateQuiz: async (id, numQuestions = 10) => {
    const response = await apiClient.post(`/programs/${id}/generate-quiz?num_questions=${numQuestions}`);
    return response.data;
  },

  getQuiz: async (id) => {
    const response = await apiClient.get(`/programs/${id}/quiz`);
    return response.data;
  },

  deleteQuiz: async (id) => {
    const response = await apiClient.delete(`/programs/${id}/quiz`);
    return response.data;
  },

  submitQuiz: async (id, answers) => {
    const response = await apiClient.post(`/programs/${id}/quiz/submit`, { answers });
    return response.data;
  },

  getQuizResult: async (id) => {
    const response = await apiClient.get(`/programs/${id}/quiz/result`);
    return response.data;
  },

  // Summary endpoints — async job com polling (Cloudflare corta a 100s).
  // O POST retorna { job_id } imediatamente e o cliente faz polling do status
  // até 'done' ou 'error'. Timeout total configurável (default 5min).
  generateSummary: async (id, { pollIntervalMs = 3500, timeoutMs = 300000 } = {}) => {
    const start = await apiClient.post(`/programs/${id}/summary/generate`);
    const jobId = start.data?.job_id;
    if (!jobId) {
      // Fallback: se o backend antigo devolveu o resumo direto, aceita.
      if (start.data?.contents || start.data?.overview) return start.data;
      throw new Error('Backend não devolveu job_id do resumo');
    }
    const startedAt = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > timeoutMs) {
        const err = new Error('Tempo esgotado gerando resumo (>5min). Tente novamente.');
        err.code = 'ESUMMARYTIMEOUT';
        throw err;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      // eslint-disable-next-line no-await-in-loop
      const st = await apiClient.get(`/programs/${id}/summary/generate/status/${jobId}`);
      const status = st.data?.status;
      if (status === 'done') return st.data?.result || {};
      if (status === 'error') {
        const err = new Error(st.data?.error || 'Falha ao gerar resumo');
        err.response = { data: { detail: st.data?.error }, status: st.data?.error_code || 500 };
        throw err;
      }
      // pending / running → continua polling
    }
  },
  getSummary: async (id) => {
    const response = await apiClient.get(`/programs/${id}/summary`);
    return response.data;
  },
  updateSummary: async (id, data) => {
    const response = await apiClient.put(`/programs/${id}/summary`, data);
    return response.data;
  },
  
  // Personas endpoints
  getPersonas: async (id) => {
    const response = await apiClient.get(`/programs/${id}/personas`);
    return response.data;
  },
  
  addPersona: async (id, personaId) => {
    const response = await apiClient.post(`/programs/${id}/personas/${personaId}`);
    return response.data;
  },
  
  removePersona: async (id, personaId) => {
    const response = await apiClient.delete(`/programs/${id}/personas/${personaId}`);
    return response.data;
  },
  
  // User endpoints
  hasPrograms: async () => {
    const response = await apiClient.get('/programs/has-programs');
    return response.data;
  },

  urgentCount: async () => {
    const response = await apiClient.get('/programs/urgent-count', { timeout: 20000 });
    return response.data; // { urgent, expired, total, needs_attention }
  },

  getMyPrograms: async () => {
    const response = await apiClient.get('/programs/my-programs');
    return response.data;
  },
  
  getMyProgramDetail: async (id) => {
    const response = await apiClient.get(`/programs/my-programs/${id}`);
    return response.data;
  },
};

// Gamification API
export const gamificationAPI = {
  getSettings: async () => {
    const response = await apiClient.get('/gamification/settings');
    return response.data;
  },
  
  updateSettings: async (settings) => {
    const response = await apiClient.put('/gamification/settings', settings);
    return response.data;
  },

  getCompanySettings: async (companyId) => {
    const response = await apiClient.get(`/gamification/company/${companyId}/settings`);
    return response.data;
  },

  updateCompanySettings: async (companyId, settings) => {
    const response = await apiClient.put(`/gamification/company/${companyId}/settings`, settings);
    return response.data;
  },

  getMyStatus: async () => {
    const response = await apiClient.get('/gamification/my-status');
    return response.data;
  },
  
  getMyScore: async () => {
    const response = await apiClient.get('/gamification/my-score');
    return response.data;
  },
  
  syncScore: async () => {
    const response = await apiClient.post('/gamification/sync');
    return response.data;
  },
  
  getRanking: async (companyId, limit = 10) => {
    const response = await apiClient.get(`/gamification/ranking/${companyId}`, { params: { limit } });
    return response.data;
  },
  
  getAllScores: async (companyId = null) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/gamification/scores', { params });
    return response.data;
  }
};

// Audit Logs API
export const auditLogsAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/audit-logs', { params });
    return response.data;
  },

  getActions: async () => {
    const response = await apiClient.get('/audit-logs/actions');
    return response.data;
  },

  getStats: async () => {
    const response = await apiClient.get('/audit-logs/stats');
    return response.data;
  },

  getUserLogs: async (userId, limit = 50) => {
    const response = await apiClient.get(`/audit-logs/user/${userId}`, { params: { limit } });
    return response.data;
  },

  exportCSV: async (params = {}) => {
    const response = await apiClient.get('/audit-logs/export', { params, responseType: 'blob' });
    return response.data;
  },
};

export const telemetryAPI = {
  getRecent: async (limit = 200) => {
    const response = await apiClient.get('/admin/telemetry/frontend-errors/recent', { params: { limit } });
    return response.data;
  },
};

// Smishing (SMS Simulator) — Zenvia integration
export const smishingAPI = {
  getConfig: async () => (await apiClient.get('/admin/smishing/config')).data,
  updateConfig: async (payload) => (await apiClient.post('/admin/smishing/config', payload)).data,
  listConsents: async (activeOnly = true) => (await apiClient.get('/admin/smishing/consents', { params: { active_only: activeOnly } })).data,
  addConsent: async (payload) => (await apiClient.post('/admin/smishing/consents', payload)).data,
  revokeConsent: async (id) => (await apiClient.delete(`/admin/smishing/consents/${id}`)).data,
  listOptouts: async () => (await apiClient.get('/admin/smishing/optouts')).data,
  sendOne: async (payload) => (await apiClient.post('/admin/smishing/send', payload)).data,
  sendBatch: async (messages) => (await apiClient.post('/admin/smishing/send/batch', { messages })).data,
  history: async (limit = 100, campaignId = null) => {
    const params = { limit };
    if (campaignId) params.campaign_id = campaignId;
    return (await apiClient.get('/admin/smishing/history', { params })).data;
  },
  listTemplates: async () => (await apiClient.get('/admin/smishing/templates')).data,
  prepareBody: async (payload) => (await apiClient.post('/admin/smishing/prepare-body', payload)).data,
};

// Attacker Console — live SOC feed
export const attackerConsoleAPI = {
  recent: async (limit = 50) => (await apiClient.get('/admin/attacker-console/recent', { params: { limit } })).data,
  stats: async () => (await apiClient.get('/admin/attacker-console/stats')).data,
  streamUrl: () => {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    return `${base}/api/admin/attacker-console/stream`;
  },
};

// Featured Videos API
export const featuredAPI = {
  // Get featured videos for homepage
  getFeatured: async () => {
    const response = await axios.get(`${API}/featured`);
    return response.data;
  },

  // Admin: Get all videos with featured status
  getAllWithStatus: async () => {
    const response = await apiClient.get('/featured/admin/all');
    return response.data;
  },

  // Admin: Toggle featured status
  toggleFeatured: async (videoId) => {
    const response = await apiClient.put(`/featured/${videoId}/toggle`);
    return response.data;
  },

  // Admin: Reorder featured videos
  reorder: async (order) => {
    const response = await apiClient.put('/featured/reorder', { order });
    return response.data;
  },

  // Admin: Bulk set featured videos
  bulkSet: async (videoIds) => {
    const response = await apiClient.post('/featured/bulk-set', { videoIds });
    return response.data;
  },
};

// Complementary Content API
export const complementaryContentAPI = {
  // User: Get active complementary content for a video
  getByVideo: async (videoId) => {
    const response = await apiClient.get(`/conteudos/${videoId}/complementary`);
    return response.data;
  },

  // Admin: Get all complementary content (including inactive)
  getByVideoAdmin: async (videoId) => {
    const response = await apiClient.get(`/conteudos/${videoId}/complementary/admin`);
    return response.data;
  },

  // Admin: Create complementary content
  create: async (videoId, data) => {
    const response = await apiClient.post(`/conteudos/${videoId}/complementary`, data);
    return response.data;
  },

  // Admin: Update complementary content
  update: async (id, data) => {
    const response = await apiClient.put(`/complementary/${id}`, data);
    return response.data;
  },

  // Admin: Delete complementary content
  delete: async (id) => {
    const response = await apiClient.delete(`/complementary/${id}`);
    return response.data;
  },

  // Admin: Reorder complementary content
  reorder: async (videoId, order) => {
    const response = await apiClient.put(`/conteudos/${videoId}/complementary/reorder`, { order });
    return response.data;
  },

  // Admin: Analyze PDF content with AI (extract text, generate title/description)
  analyzePdf: async (pdfUrl) => {
    const response = await apiClient.post(`/complementary/analyze-pdf`, { pdf_url: pdfUrl });
    return response.data;
  },

  // Admin: Check if a transcript is available before generating a podcast
  transcriptStatus: async (videoId) => {
    const response = await apiClient.get(`/complementary-ai/${videoId}/transcript-status`);
    return response.data;
  },

  // Admin: Generate AI Podcast (transcript → Claude script → OpenAI TTS → S3 → complementary)
  generatePodcast: async (videoId, options = {}) => {
    const response = await apiClient.post(`/complementary-ai/${videoId}/generate-podcast`, options, { timeout: 180000 });
    return response.data;
  },
};

// Quiz API
export const quizAPI = {
  // Admin: Save quiz questions
  saveQuestions: async (quizId, questions) => {
    const response = await apiClient.post(`/quiz/${quizId}/questions`, { questions });
    return response.data;
  },

  // Admin: Get quiz questions with answers
  getQuestionsAdmin: async (quizId) => {
    const response = await apiClient.get(`/quiz/${quizId}/questions/admin`);
    return response.data;
  },

  // User: Get quiz questions without answers
  getQuestions: async (quizId) => {
    const response = await apiClient.get(`/quiz/${quizId}/questions`);
    return response.data;
  },

  // User: Submit quiz attempt
  submitAttempt: async (quizId, answers, timeTaken) => {
    const response = await apiClient.post(`/quiz/${quizId}/attempt`, { 
      answers, 
      time_taken: timeTaken 
    });
    return response.data;
  },

  // User: Get my attempts
  getMyAttempts: async (quizId) => {
    const response = await apiClient.get(`/quiz/${quizId}/attempts/my`);
    return response.data;
  },

  // Admin: Get quiz stats
  getStats: async (quizId) => {
    const response = await apiClient.get(`/quiz/${quizId}/stats`);
    return response.data;
  },
};

// Simulator API
export const simulatorAPI = {
  // Admin: Save simulator data
  saveData: async (simulatorId, data) => {
    const response = await apiClient.post(`/simulator/${simulatorId}/data`, data);
    return response.data;
  },

  // User: Get simulator data
  getData: async (simulatorId) => {
    const response = await apiClient.get(`/simulator/${simulatorId}/data`);
    return response.data;
  },

  // User: Save progress
  saveProgress: async (simulatorId, progress) => {
    const response = await apiClient.post(`/simulator/${simulatorId}/progress`, progress);
    return response.data;
  },

  // User: Get my progress
  getMyProgress: async (simulatorId) => {
    const response = await apiClient.get(`/simulator/${simulatorId}/progress/my`);
    return response.data;
  },

  // Admin: Get simulator stats
  getStats: async (simulatorId) => {
    const response = await apiClient.get(`/simulator/${simulatorId}/stats`);
    return response.data;
  },
};

// Synthesia API
export const synthesiaAPI = {
  // Get available avatars
  getAvatars: async () => {
    const response = await apiClient.get('/synthesia/avatars');
    return response.data;
  },

  // Get available voices
  getVoices: async () => {
    const response = await apiClient.get('/synthesia/voices');
    return response.data;
  },

  // Get available templates
  getTemplates: async () => {
    const response = await apiClient.get('/synthesia/templates');
    return response.data;
  },

  // Create video
  createVideo: async (videoData) => {
    const response = await apiClient.post('/synthesia/videos', videoData);
    return response.data;
  },

  // Get video status
  getVideoStatus: async (trackingId) => {
    const response = await apiClient.get(`/synthesia/videos/${trackingId}/status`);
    return response.data;
  },

  // List videos
  listVideos: async (params) => {
    const response = await apiClient.get('/synthesia/videos', { params });
    return response.data;
  },

  // Get available videos for content selection (completed & ready to use)
  getAvailableVideos: async (includePublished = false) => {
    const response = await apiClient.get('/synthesia/videos/available', { 
      params: { all: includePublished } 
    });
    return response.data;
  },

  // Sync all videos from Synthesia account
  syncVideos: async () => {
    const response = await apiClient.post('/synthesia/sync');
    return response.data;
  },

  // Publish video to platform
  publishVideo: async (trackingId) => {
    const response = await apiClient.post(`/synthesia/videos/${trackingId}/publish`);
    return response.data;
  },

  // Delete video
  deleteVideo: async (trackingId) => {
    const response = await apiClient.delete(`/synthesia/videos/${trackingId}`);
    return response.data;
  },
};

// Personas API
export const personasAPI = {
  // List all personas
  list: async (params = {}) => {
    const response = await apiClient.get('/personas', { params });
    return response.data;
  },

  // Get persona by ID
  get: async (id) => {
    const response = await apiClient.get(`/personas/${id}`);
    return response.data;
  },

  // Create new persona
  create: async (data) => {
    const response = await apiClient.post('/personas', data);
    return response.data;
  },

  // Update persona
  update: async (id, data) => {
    const response = await apiClient.put(`/personas/${id}`, data);
    return response.data;
  },

  // Delete persona
  delete: async (id) => {
    const response = await apiClient.delete(`/personas/${id}`);
    return response.data;
  },

  // Get users associated with persona
  getUsers: async (id) => {
    const response = await apiClient.get(`/personas/${id}/users`);
    return response.data;
  },

  // Get persona statistics
  getStats: async (id) => {
    const response = await apiClient.get(`/personas/${id}/stats`);
    return response.data;
  },

  // Associate user with persona
  associateUser: async (personaId, userId) => {
    const response = await apiClient.post(`/personas/${personaId}/users/${userId}`);
    return response.data;
  },

  // Remove user from persona
  removeUser: async (personaId, userId) => {
    const response = await apiClient.delete(`/personas/${personaId}/users/${userId}`);
    return response.data;
  },
};

// Company Config API (Positions and Departments)
export const companyConfigAPI = {
  // Positions
  getPositions: async (companyId) => {
    const response = await apiClient.get('/company-config/positions', { params: { company_id: companyId } });
    return response.data;
  },
  
  createPosition: async (data) => {
    const response = await apiClient.post('/company-config/positions', data);
    return response.data;
  },
  
  updatePosition: async (id, data) => {
    const response = await apiClient.put(`/company-config/positions/${id}`, data);
    return response.data;
  },
  
  deletePosition: async (id) => {
    const response = await apiClient.delete(`/company-config/positions/${id}`);
    return response.data;
  },
  
  // Departments
  getDepartments: async (companyId) => {
    const response = await apiClient.get('/company-config/departments', { params: { company_id: companyId } });
    return response.data;
  },
  
  createDepartment: async (data) => {
    const response = await apiClient.post('/company-config/departments', data);
    return response.data;
  },
  
  updateDepartment: async (id, data) => {
    const response = await apiClient.put(`/company-config/departments/${id}`, data);
    return response.data;
  },
  
  deleteDepartment: async (id) => {
    const response = await apiClient.delete(`/company-config/departments/${id}`);
    return response.data;
  },
};

// Dominios Padrão API
export const dominiosAPI = {
  list: async (tipo = null) => {
    const params = tipo ? { tipo } : {};
    const response = await apiClient.get('/dominios', { params });
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/dominios', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/dominios/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/dominios/${id}`);
    return response.data;
  },
  resolve: async (tipo, companyId = null) => {
    const params = { tipo };
    if (companyId) params.company_id = companyId;
    const response = await apiClient.get('/dominios/resolve', { params });
    return response.data;
  },
};

// Security Events API
const securityEventsAPI = {
  // List events
  list: async (params = {}) => {
    const response = await apiClient.get('/security-events', { params });
    return response.data;
  },

  // Get event by ID
  get: async (id) => {
    const response = await apiClient.get(`/security-events/${id}`);
    return response.data;
  },

  // Create new event (admin only)
  create: async (data) => {
    const response = await apiClient.post('/security-events', data);
    return response.data;
  },

  // Update event status
  updateStatus: async (id, status) => {
    const response = await apiClient.put(`/security-events/${id}/status`, { status });
    return response.data;
  },

  // Delete event
  delete: async (id) => {
    const response = await apiClient.delete(`/security-events/${id}`);
    return response.data;
  }
};

// Risk Analysis API
const riskAnalysisAPI = {
  // Get overview (admin)
  getOverview: async (companyId = null) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/risk-analysis/overview', { params });
    return response.data;
  },

  // Get user analysis
  getUserAnalysis: async (userId) => {
    const response = await apiClient.get(`/risk-analysis/user/${userId}`);
    return response.data;
  },

  // Get department analysis
  getDepartmentAnalysis: async (departamento) => {
    const response = await apiClient.get(`/risk-analysis/department/${departamento}`);
    return response.data;
  },

  // Get recommendations
  getRecommendations: async (userId) => {
    const response = await apiClient.get(`/risk-analysis/recommendations/${userId}`);
    return response.data;
  }
};

// Company Departments API
export const companyDepartmentsAPI = {
  // Listar departamentos de uma empresa
  getDepartments: async (companyId, filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await apiClient.get(`/companies/${companyId}/departments?${params}`);
    return response.data;
  },

  // Obter departamento específico
  getDepartment: async (companyId, departmentId) => {
    const response = await apiClient.get(`/companies/${companyId}/departments/${departmentId}`);
    return response.data;
  },

  // Criar departamento
  createDepartment: async (companyId, data) => {
    const response = await apiClient.post(`/companies/${companyId}/departments`, data);
    return response.data;
  },

  // Atualizar departamento
  updateDepartment: async (companyId, departmentId, data) => {
    const response = await apiClient.put(`/companies/${companyId}/departments/${departmentId}`, data);
    return response.data;
  },

  // Deletar departamento
  deleteDepartment: async (companyId, departmentId) => {
    const response = await apiClient.delete(`/companies/${companyId}/departments/${departmentId}`);
    return response.data;
  }
};

// Company Roles API
export const companyRolesAPI = {
  // Listar funções de uma empresa
  getRoles: async (companyId, filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await apiClient.get(`/companies/${companyId}/roles?${params}`);
    return response.data;
  },

  // Obter função específica
  getRole: async (companyId, roleId) => {
    const response = await apiClient.get(`/companies/${companyId}/roles/${roleId}`);
    return response.data;
  },

  // Criar função
  createRole: async (companyId, data) => {
    const response = await apiClient.post(`/companies/${companyId}/roles`, data);
    return response.data;
  },

  // Atualizar função
  updateRole: async (companyId, roleId, data) => {
    const response = await apiClient.put(`/companies/${companyId}/roles/${roleId}`, data);
    return response.data;
  },

  // Deletar função
  deleteRole: async (companyId, roleId) => {
    const response = await apiClient.delete(`/companies/${companyId}/roles/${roleId}`);
    return response.data;
  }
};

// Blog API
export const blogAPI = {
  // Public endpoints
  getPosts: async (params = {}) => {
    const response = await axios.get(`${API}/blog/posts`, { params });
    return response.data;
  },

  getPostBySlug: async (slug) => {
    const response = await axios.get(`${API}/blog/posts/${slug}`);
    return response.data;
  },

  getRelatedPosts: async (slug, limit = 4) => {
    const response = await axios.get(`${API}/blog/posts/${slug}/related`, { params: { limit } });
    return response.data;
  },

  getCategories: async () => {
    const response = await axios.get(`${API}/blog/categories`);
    return response.data;
  },

  getConfig: async () => {
    const response = await axios.get(`${API}/blog/config`);
    return response.data;
  },

  // Admin endpoints
  getAdminPosts: async (params = {}) => {
    const response = await apiClient.get('/blog/admin/posts', { params });
    return response.data;
  },

  getAdminPost: async (id) => {
    const response = await apiClient.get(`/blog/admin/posts/${id}`);
    return response.data;
  },

  createPost: async (postData) => {
    const response = await apiClient.post('/blog/admin/posts', postData);
    return response.data;
  },

  updatePost: async (id, postData) => {
    const response = await apiClient.put(`/blog/admin/posts/${id}`, postData);
    return response.data;
  },

  deletePost: async (id) => {
    const response = await apiClient.delete(`/blog/admin/posts/${id}`);
    return response.data;
  },

  getSettings: async () => {
    const response = await apiClient.get('/blog/admin/settings');
    return response.data;
  },

  updateSettings: async (settings) => {
    const response = await apiClient.put('/blog/admin/settings', { settings });
    return response.data;
  },

  getLogs: async () => {
    const response = await apiClient.get('/blog/admin/logs');
    return response.data;
  },

  generate: async (data) => {
    // Geração de artigo + imagem GPT Image 1 pode levar 60-120s: sobrescreve o timeout global de 30s.
    const response = await apiClient.post('/blog/admin/generate', data, { timeout: 180000 });
    return response.data;
  },

  aiAssist: async (data) => {
    const response = await apiClient.post('/blog/admin/ai-assist', data);
    return response.data;
  }
};

// Marketplace API
const marketplaceAPI = {
  // Products
  getAllProducts: async () => {
    const response = await apiClient.get('/marketplace/products');
    return response.data;
  },
  
  getProduct: async (id) => {
    const response = await apiClient.get(`/marketplace/products/${id}`);
    return response.data;
  },
  
  createProduct: async (productData) => {
    const response = await apiClient.post('/marketplace/products', productData);
    return response.data;
  },
  
  updateProduct: async (id, productData) => {
    const response = await apiClient.put(`/marketplace/products/${id}`, productData);
    return response.data;
  },
  
  deleteProduct: async (id) => {
    const response = await apiClient.delete(`/marketplace/products/${id}`);
    return response.data;
  },
  
  // Content-Product relationships
  getContentProducts: async (contentType, contentId, adminView = false) => {
    const params = adminView ? '?admin_view=true' : '';
    const response = await apiClient.get(`/marketplace/content/${contentType}/${contentId}/products${params}`);
    return response.data;
  },
  
  linkProductsToContent: async (contentType, contentId, products) => {
    const response = await apiClient.post(
      `/marketplace/content/${contentType}/${contentId}/products`,
      { products }
    );
    return response.data;
  },
  
  // Analytics
  trackClick: async (productId, contentId, contentType) => {
    const response = await apiClient.post(`/marketplace/products/${productId}/click`, {
      content_id: contentId,
      content_type: contentType
    });
    return response.data;
  },
  
  getAnalytics: async (days = 30) => {
    const response = await apiClient.get('/marketplace/analytics/products', {
      params: { days }
    });
    return response.data;
  },

  getAnonymousVsLoggedAnalytics: async (days = 30) => {
    const response = await apiClient.get('/marketplace/analytics/anonymous-vs-logged', {
      params: { days }
    });
    return response.data;
  },

  // Offers Text Config
  getGlobalOffersTextConfig: async () => {
    const response = await apiClient.get('/marketplace/offers-text-config/global');
    return response.data;
  },

  updateGlobalOffersTextConfig: async (data) => {
    const response = await apiClient.put('/marketplace/offers-text-config/global', data);
    return response.data;
  },

  getMergedOffersTextConfig: async (contentType, contentId) => {
    const response = await apiClient.get(`/marketplace/offers-text-config/${contentType}/${contentId}`);
    return response.data;
  },

  updateContentOffersTextConfig: async (contentType, contentId, data) => {
    const response = await apiClient.put(`/marketplace/offers-text-config/${contentType}/${contentId}`, data);
    return response.data;
  },

  deleteContentOffersTextConfig: async (contentType, contentId) => {
    const response = await apiClient.delete(`/marketplace/offers-text-config/${contentType}/${contentId}`);
    return response.data;
  },

  // Public endpoints (no auth required, for anonymous visitors)
  getPublicContentProducts: async (contentType, contentId) => {
    const response = await axios.get(`${API}/marketplace/public/content/${contentType}/${contentId}/products`);
    return response.data;
  },

  getPublicOffersTextConfig: async (contentType, contentId) => {
    const response = await axios.get(`${API}/marketplace/public/offers-text-config/${contentType}/${contentId}`);
    return response.data;
  },

  trackPublicClick: async (productId, contentId, contentType) => {
    const response = await axios.post(`${API}/marketplace/public/products/${productId}/click`, {
      content_id: contentId,
      content_type: contentType
    });
    return response.data;
  },

  generateProductImage: async (productName, productDescription, customPrompt, options = {}) => {
    const response = await apiClient.post('/marketplace/products/generate-image', {
      product_name: productName,
      product_description: productDescription,
      custom_prompt: customPrompt || '',
      render_title: !!options.renderTitle,
      title_text: options.renderTitle ? (options.titleText || '').trim() : null,
    }, { timeout: 120000 });
    return response.data;
  },

  aiAssistProduct: async (payload) => {
    const response = await apiClient.post('/marketplace/products/ai-assist', payload, { timeout: 90000 });
    return response.data;
  },

  // Public catalog (no auth)
  getCatalog: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.product_type) params.append('product_type', filters.product_type);
    if (filters.category) params.append('category', filters.category);
    if (filters.seller_type) params.append('seller_type', filters.seller_type);
    if (filters.search) params.append('search', filters.search);
    const response = await axios.get(`${API}/marketplace/catalog?${params.toString()}`);
    return response.data;
  },

  getCatalogProduct: async (productId) => {
    const response = await axios.get(`${API}/marketplace/catalog/${productId}`);
    return response.data;
  },

  submitQuote: async (productId, data) => {
    const response = await axios.post(`${API}/marketplace/catalog/${productId}/quote`, data);
    return response.data;
  },

  // Admin quotes
  getQuotes: async (status) => {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get(`/marketplace/quotes${params}`);
    return response.data;
  },

  updateQuoteStatus: async (quoteId, data) => {
    const response = await apiClient.put(`/marketplace/quotes/${quoteId}`, data);
    return response.data;
  },
};

// Promotional Ads API
const adsAPI = {
  getCatalog: async () => {
    const response = await apiClient.get('/ads/catalog');
    return response.data;
  },
  createAd: async (data) => {
    const response = await apiClient.post('/ads/catalog', data);
    return response.data;
  },
  updateAd: async (id, data) => {
    const response = await apiClient.put(`/ads/catalog/${id}`, data);
    return response.data;
  },
  deleteAd: async (id) => {
    const response = await apiClient.delete(`/ads/catalog/${id}`);
    return response.data;
  },
  getContentAds: async (contentId) => {
    const response = await apiClient.get(`/ads/content/${contentId}`);
    return response.data;
  },
  linkAdsToContent: async (contentId, ads) => {
    const response = await apiClient.post(`/ads/content/${contentId}`, { ads });
    return response.data;
  },
  getPublicAds: async (contentId) => {
    // Fev/2026: envia Bearer se logado — backend usa isso pra decidir se
    // o usuário se enquadra em plano free/paid ou é anônimo.
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.get(`${API}/ads/public/${contentId}`, { headers });
    return response.data;
  },
  trackImpression: async (adId, contentId, action) => {
    await axios.post(`${API}/ads/track`, { ad_id: adId, content_id: contentId, action });
  },
  getAnalytics: async () => {
    const response = await apiClient.get('/ads/analytics');
    return response.data;
  }
};

export default {
  auth: authAPI,
  video: videoAPI,
  questionnaire: questionnaireAPI,
  certificate: certificateAPI,
  admin: adminAPI,
  gamification: gamificationAPI,
  auditLogs: auditLogsAPI,
  quiz: quizAPI,
  simulator: simulatorAPI,
  complementaryContent: complementaryContentAPI,
  featured: featuredAPI,
  synthesia: synthesiaAPI,
  personas: personasAPI,
  companyAPI: companyAPI,
  programAPI: programAPI,
  companyConfig: companyConfigAPI,
  securityEvents: securityEventsAPI,
  riskAnalysis: riskAnalysisAPI,
  companyDepartments: companyDepartmentsAPI,
  companyRoles: companyRolesAPI,
  blog: blogAPI,
  marketplace: marketplaceAPI,
  ads: adsAPI
};

export { securityEventsAPI, riskAnalysisAPI, marketplaceAPI, adsAPI, apiClient };

// ─── Pílulas de Conhecimento por E-mail ───
const KP = '/knowledge-pills';
export const accessibilityAPI = {
  adminGetConfig: (scope, companyId) => apiClient.get('/accessibility/admin/config', { params: { scope, company_id: companyId || undefined } }).then(r => r.data),
  adminSaveConfig: (body) => apiClient.put('/accessibility/admin/config', body).then(r => r.data),
};


export const knowledgePillsAPI = {
  listOperations: () => apiClient.get(`${KP}/operations`).then(r => r.data),
  createOperation: (data) => apiClient.post(`${KP}/operations`, data).then(r => r.data),
  updateOperation: (id, data) => apiClient.put(`${KP}/operations/${id}`, data).then(r => r.data),
  deleteOperation: (id) => apiClient.delete(`${KP}/operations/${id}`).then(r => r.data),
  listPrograms: (operationId) => apiClient.get(`${KP}/operations/${operationId}/programs`).then(r => r.data),
  createProgram: (operationId, data) => apiClient.post(`${KP}/operations/${operationId}/programs`, data).then(r => r.data),
  getProgram: (programId) => apiClient.get(`${KP}/programs/${programId}`).then(r => r.data),
  updateProgram: (programId, data) => apiClient.put(`${KP}/programs/${programId}`, data).then(r => r.data),
  deleteProgram: (programId) => apiClient.delete(`${KP}/programs/${programId}`).then(r => r.data),
  activateProgram: (programId) => apiClient.post(`${KP}/programs/${programId}/activate`).then(r => r.data),
  pauseProgram: (programId) => apiClient.post(`${KP}/programs/${programId}/pause`).then(r => r.data),
  addPill: (programId, data) => apiClient.post(`${KP}/programs/${programId}/pills`, data).then(r => r.data),
  updatePill: (pillId, data) => apiClient.put(`${KP}/pills/${pillId}`, data).then(r => r.data),
  deletePill: (pillId) => apiClient.delete(`${KP}/pills/${pillId}`).then(r => r.data),
  reorderPills: (programId, pillIds) => apiClient.put(`${KP}/programs/${programId}/pills/reorder`, { pill_ids: pillIds }).then(r => r.data),
  sendTest: (pillId, email) => apiClient.post(`${KP}/pills/${pillId}/send-test`, { email }).then(r => r.data),
  previewPill: (pillId) => apiClient.get(`${KP}/pills/${pillId}/preview`).then(r => r.data),
  getRecipients: (programId) => apiClient.get(`${KP}/programs/${programId}/recipients`).then(r => r.data),
  setRecipients: (programId, recipients) => apiClient.put(`${KP}/programs/${programId}/recipients`, { recipients }).then(r => r.data),
  companyUsers: (operationId) => apiClient.get(`${KP}/operations/${operationId}/company-users`).then(r => r.data),
  aiGenerateProgram: (theme, numPills) => apiClient.post(`${KP}/ai/generate-program`, { theme, num_pills: numPills }).then(r => r.data),
  aiSuggestContent: (theme) => apiClient.post(`${KP}/ai/suggest-content`, { theme }).then(r => r.data),
  aiSpamScore: (subject, text) => apiClient.post(`${KP}/ai/spam-score`, { subject, text }).then(r => r.data),
  aiSummarize: (topic, maxWords) => apiClient.post(`${KP}/ai/summarize`, { topic, max_words: maxWords || 90 }).then(r => r.data),
  resendUnopened: (pillId) => apiClient.post(`${KP}/pills/${pillId}/resend-unopened`).then(r => r.data),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    return apiClient.post(`${KP}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  runCron: () => apiClient.post(`${KP}/cron/run`).then(r => r.data),
};


// ─── Ad Campaigns API (Google Ads style) ───
export const adCampaignsAPI = {
  // Stats
  getStats: async () => {
    const r = await apiClient.get('/ad-campaigns/stats');
    return r.data;
  },
  // Advertisers
  getAdvertisers: async () => {
    const r = await apiClient.get('/ad-campaigns/advertisers');
    return r.data;
  },
  createAdvertiser: async (data) => {
    const r = await apiClient.post('/ad-campaigns/advertisers', data);
    return r.data;
  },
  updateAdvertiser: async (id, data) => {
    const r = await apiClient.put(`/ad-campaigns/advertisers/${id}`, data);
    return r.data;
  },
  deleteAdvertiser: async (id) => {
    const r = await apiClient.delete(`/ad-campaigns/advertisers/${id}`);
    return r.data;
  },
  // Campaigns
  getCampaigns: async (params) => {
    const r = await apiClient.get('/ad-campaigns/campaigns', { params });
    return r.data;
  },
  createCampaign: async (data) => {
    const r = await apiClient.post('/ad-campaigns/campaigns', data);
    return r.data;
  },
  updateCampaign: async (id, data) => {
    const r = await apiClient.put(`/ad-campaigns/campaigns/${id}`, data);
    return r.data;
  },
  updateCampaignStatus: async (id, data) => {
    const r = await apiClient.put(`/ad-campaigns/campaigns/${id}/status`, data);
    return r.data;
  },
  deleteCampaign: async (id) => {
    const r = await apiClient.delete(`/ad-campaigns/campaigns/${id}`);
    return r.data;
  },
  // Creatives
  getCreatives: async (campaignId) => {
    const r = await apiClient.get(`/ad-campaigns/campaigns/${campaignId}/creatives`);
    return r.data;
  },
  createCreative: async (campaignId, data) => {
    const r = await apiClient.post(`/ad-campaigns/campaigns/${campaignId}/creatives`, data);
    return r.data;
  },
  updateCreative: async (id, data) => {
    const r = await apiClient.put(`/ad-campaigns/creatives/${id}`, data);
    return r.data;
  },
  deleteCreative: async (id) => {
    const r = await apiClient.delete(`/ad-campaigns/creatives/${id}`);
    return r.data;
  },
  // Fase 2: Ad Serving & Tracking
  serveAd: async (contentId, placement = 'pre_roll') => {
    const r = await axios.get(`${API}/ad-campaigns/serve/${contentId}?placement=${placement}`);
    return r.data;
  },
  trackCampaignImpression: async (campaignId, creativeId, contentId, action) => {
    await axios.post(`${API}/ad-campaigns/track`, {
      campaign_id: campaignId, creative_id: creativeId,
      content_id: contentId, action
    });
  },
  // Fase 2: Performance
  getPerformance: async () => {
    const r = await apiClient.get('/ad-campaigns/performance');
    return r.data;
  },
  getPerformanceDetails: async (campaignId) => {
    const r = await apiClient.get(`/ad-campaigns/performance/${campaignId}/details`);
    return r.data;
  },
  // Fase 3: Consolidated Dashboard
  getDashboard: async () => {
    const r = await apiClient.get('/ad-campaigns/dashboard');
    return r.data;
  },
  // Fase 3: PDF Reports
  getAdvertiserReportUrl: (advId) => `${API}/ad-campaigns/report/advertiser/${advId}`,
  getCampaignReportUrl: (campId) => `${API}/ad-campaigns/report/campaign/${campId}`,
};


